import { useState, useEffect, useCallback } from 'react';
import { API_URL, apiClient } from '../config/api';

export const useAuth = (showMessage) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      return localStorage.getItem('token');
    }
    return null;
  });
  const [loading, setLoading] = useState(true);

  // Login form state
  const [loginData, setLoginData] = useState({ username: '', password: '', client_code: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoadingAuth, setIsLoadingAuth] = useState(false);

  // TOTP & 2FA state
  const [requiresTOTP, setRequiresTOTP] = useState(false);
  const [requiresTOTPSetup, setRequiresTOTPSetup] = useState(false);
  const [totpToken, setTotpToken] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [totpSetupData, setTotpSetupData] = useState(null);
  const [setupToken, setSetupToken] = useState('');

  // Mandatory first-login master password change state
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [forcePasswordToken, setForcePasswordToken] = useState('');
  const [forcePasswordForm, setForcePasswordForm] = useState({
    new_password: '',
    confirm_password: '',
  });

  // Password change state
  const [profilePasswordForm, setProfilePasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Validate session token on initial mount
  const checkAuth = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient('/api/auth/me');
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Session check error:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Primary Login Handler
  const handleLogin = async (e) => {
    if (e) e.preventDefault();
    setIsLoadingAuth(true);
    setLoginError('');

    try {
      const payload = {
        username: loginData.username,
        password: loginData.password,
        client_code: loginData.client_code,
        ...(requiresTOTP && !useBackupCode && { 
          totp_token: totpToken,
          twofa_token: totpToken,
        }),
        ...(requiresTOTP && useBackupCode && { 
          backup_code: backupCode,
        }),
      };

      const response = await apiClient('/api/auth/login', {
        method: 'POST',
        body: payload,
      });

      const data = await response.json();

      if (data.requires_password_change) {
        setRequiresPasswordChange(true);
        setForcePasswordToken(data.temp_token || '');
        setRequiresTOTP(false);
        setRequiresTOTPSetup(false);
        setLoginError('');
      } else if (data.requires_totp_setup) {
        setRequiresTOTPSetup(true);
        setRequiresTOTP(false);
        setRequiresPasswordChange(false);
        setSetupToken(data.setup_token || '');
        setLoginError('');
      } else if (data.requires_totp) {
        setRequiresTOTP(true);
        setRequiresTOTPSetup(false);
        setRequiresPasswordChange(false);
        setLoginError('');
      } else if (response.ok && (data.token || data.access_token)) {
        // Successful login
        const authToken = data.token || data.access_token;
        localStorage.setItem('token', authToken);
        setToken(authToken);
        setUser(data.user);
        setRequiresTOTP(false);
        setRequiresTOTPSetup(false);
        setRequiresPasswordChange(false);
        setTotpToken('');
        setBackupCode('');
        setLoginError('');
        if (showMessage) showMessage('Welcome back! Successfully signed in.', 'success');
      } else {
        setLoginError(data.detail || data.error || 'Invalid credentials or client code.');
      }
    } catch (error) {
      setLoginError('Network error. Please check your connection.');
    } finally {
      setIsLoadingAuth(false);
    }
  };

  // Mandatory First-Login Password Change Handler
  const handleForcePasswordChange = async (e) => {
    if (e) e.preventDefault();
    setIsLoadingAuth(true);
    setLoginError('');

    if (forcePasswordForm.new_password !== forcePasswordForm.confirm_password) {
      setLoginError('New passwords do not match.');
      setIsLoadingAuth(false);
      return;
    }

    if (forcePasswordForm.new_password.length < 12) {
      setLoginError('New master password must be at least 12 characters long.');
      setIsLoadingAuth(false);
      return;
    }

    try {
      const response = await apiClient('/api/auth/force-change-password', {
        method: 'POST',
        body: {
          temp_token: forcePasswordToken,
          new_password: forcePasswordForm.new_password,
          confirm_password: forcePasswordForm.confirm_password,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        setLoginError(data.detail || data.error || 'Failed to update master password');
        return;
      }

      setRequiresPasswordChange(false);
      setForcePasswordForm({ new_password: '', confirm_password: '' });
      if (showMessage) showMessage('Master password upgraded successfully!', 'success');

      if (data.requires_totp_setup) {
        setRequiresTOTPSetup(true);
        setRequiresTOTP(false);
      } else if (data.access_token) {
        localStorage.setItem('token', data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        if (showMessage) showMessage('Welcome to NEXDESK! Master account secured.', 'success');
      }
    } catch (error) {
      setLoginError('Network error updating master password.');
    } finally {
      setIsLoadingAuth(false);
    }
  };

  // Setup TOTP Handler
  const setupTOTP = async () => {
    setIsLoadingAuth(true);
    setLoginError('');
    try {
      const response = await apiClient('/api/auth/setup-totp-login', {
        method: 'POST',
        body: {
          username: loginData.username,
          password: loginData.password,
          client_code: loginData.client_code,
        },
      });
      const data = await response.json();
      if (response.ok && (data.qr_code || data.secret)) {
        setTotpSetupData(data);
      } else {
        setLoginError(data.error || data.detail || 'Failed to initialize TOTP setup.');
      }
    } catch (error) {
      setLoginError('Network error during TOTP setup.');
    } finally {
      setIsLoadingAuth(false);
    }
  };

  // Verify TOTP Setup
  const verifyTOTPSetup = async (tokenCode) => {
    setIsLoadingAuth(true);
    setLoginError('');
    try {
      const response = await apiClient('/api/auth/verify-totp-setup-login', {
        method: 'POST',
        body: {
          username: loginData.username,
          password: loginData.password,
          client_code: loginData.client_code,
          totp_code: tokenCode,
          totp_token: tokenCode,
        },
      });

      const data = await response.json();
      if (response.ok) {
        const authToken = data.token || data.access_token;
        if (authToken) {
          localStorage.setItem('token', authToken);
          setToken(authToken);
          setUser(data.user);
        }
        setRequiresTOTPSetup(false);
        setRequiresTOTP(false);
        setTotpSetupData(null);
        if (showMessage) showMessage('Google Authenticator configured successfully!', 'success');
      } else {
        setLoginError(data.error || data.detail || 'Invalid verification code.');
      }
    } catch (error) {
      setLoginError('Failed to verify setup code.');
    } finally {
      setIsLoadingAuth(false);
    }
  };

  // Reset TOTP and generate fresh QR code
  const resetTOTP = async () => {
    setIsLoadingAuth(true);
    setLoginError('');
    try {
      const response = await apiClient('/api/auth/reset-totp-login', {
        method: 'POST',
        body: {
          username: loginData.username,
          password: loginData.password,
          client_code: loginData.client_code,
        },
      });
      const data = await response.json();
      if (response.ok && (data.qr_code || data.secret)) {
        setTotpSetupData(data);
        setRequiresTOTP(false);
        setRequiresTOTPSetup(true);
        if (showMessage) showMessage('Fresh 2FA QR code generated. Scan with Google Authenticator.', 'success');
      } else {
        setLoginError(data.error || data.detail || 'Failed to reset 2FA.');
      }
    } catch (err) {
      setLoginError('Network error resetting 2FA.');
    } finally {
      setIsLoadingAuth(false);
    }
  };

  // Change Profile Password
  const handleChangePassword = async (e) => {
    if (e) e.preventDefault();

    if (profilePasswordForm.new_password !== profilePasswordForm.confirm_password) {
      if (showMessage) showMessage('New passwords do not match.', 'error');
      return false;
    }

    if (profilePasswordForm.new_password.length < 6) {
      if (showMessage) showMessage('Password must be at least 6 characters.', 'error');
      return false;
    }

    setIsChangingPassword(true);
    try {
      const response = await apiClient('/api/auth/change-password', {
        method: 'POST',
        body: {
          current_password: profilePasswordForm.current_password,
          new_password: profilePasswordForm.new_password,
        },
      });

      if (response.ok) {
        if (showMessage) showMessage('Password changed successfully!', 'success');
        setProfilePasswordForm({ current_password: '', new_password: '', confirm_password: '' });
        return true;
      } else {
        const err = await response.json();
        if (showMessage) showMessage(err.detail || 'Failed to change password', 'error');
        return false;
      }
    } catch (error) {
      if (showMessage) showMessage('Network error updating password', 'error');
      return false;
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Logout Handler (Clears all state and storage)
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setLoginData({ username: '', password: '', client_code: '' });
    setRequiresTOTP(false);
    setRequiresTOTPSetup(false);
    setRequiresPasswordChange(false);
    setForcePasswordToken('');
    setForcePasswordForm({ new_password: '', confirm_password: '' });
    if (showMessage) showMessage('Signed out successfully.', 'info');
  };

  return {
    user,
    setUser,
    token,
    setToken,
    loading,
    loginData,
    setLoginData,
    loginError,
    setLoginError,
    isLoadingAuth,
    requiresTOTP,
    requiresTOTPSetup,
    requiresPasswordChange,
    setRequiresPasswordChange,
    forcePasswordForm,
    setForcePasswordForm,
    handleForcePasswordChange,
    totpToken,
    setTotpToken,
    backupCode,
    setBackupCode,
    useBackupCode,
    setUseBackupCode,
    totpSetupData,
    profilePasswordForm,
    setProfilePasswordForm,
    isChangingPassword,
    handleLogin,
    setupTOTP,
    verifyTOTPSetup,
    resetTOTP,
    handleChangePassword,
    handleLogout,
  };
};
